import * as Tooltip from "@radix-ui/react-tooltip";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { motion } from "framer-motion";
import clsx from "clsx";

interface TooltipProps {
  children: ReactNode;
  sideOffset?: number;
  content: string | ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  contentClassName?: string;
  wrapperClassName?: string;
}

export const CustomTooltip = ({
  children,
  content,
  sideOffset = 10,
  contentClassName,
  wrapperClassName,
  side = "top",
}: TooltipProps) => {
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if ((target as HTMLElement).closest(".sidebar-toggle-button")) {
        return;
      }

      if (tooltipRef.current && !tooltipRef.current.contains(target)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <Tooltip.Provider delayDuration={100}>
      <Tooltip.Root open={open} onOpenChange={setOpen}>
        <Tooltip.Trigger asChild onClick={() => setOpen((open) => !open)}>
          {children}
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            ref={tooltipRef}
            side={side}
            sideOffset={sideOffset}
            className={wrapperClassName}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className={clsx(
                "z-[200] px-3 py-2 text-sm rounded-[8px] bg-black/90 text-white border border-white/10 text-[14px] font-[500] font-manrope shadow-sm",
                contentClassName,
              )}
            >
              {content}
            </motion.div>
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};
